import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useMemo, useRef } from 'react'
import { AlmanacFileCalendarEvent } from 'screens/Calendar/types'
import { getEventProperties } from 'screens/Calendar/WeekViewV2/CalendarEventCard/utils'

export enum ImagePreviewSource {
  IMAGE_URL,
  DESIGN,
  NONE,
  FAIL,
  EMPTY,
  TEXT,
  LOADING,
}

export type PreviewImageState = {
  previewSource: ImagePreviewSource
  showLoading: boolean
}

type Input = {
  isDesignLoaded: boolean
  didGenerationFail?: boolean
  event: AlmanacFileCalendarEvent
  isImageLoaded: boolean
  isCopilot?: boolean
}

export const usePreviewImageState = ({
  event,
  isImageLoaded,
  isDesignLoaded,
  didGenerationFail = false,
  isCopilot = false,
}: Input): PreviewImageState => {
  const hasRenderedDesignContentRef = useRef(false)

  const { isMobile } = useResponsiveDesign()

  const state = useMemo<PreviewImageState>(() => {
    const {
      empty,
      image_url: imageUrl,
      is_generating,
      image_url_generating,
      design_content,
      width,
      height,
      content,
      is_design_blank,
    } = event

    const { isLoading, isLoadingDocPreview, isWithinPreviewImageWaitTime, showDocText } = getEventProperties(
      event,
      isImageLoaded,
      isDesignLoaded,
      isCopilot,
    )

    // Check if generation failed
    if (didGenerationFail) {
      return {
        previewSource: ImagePreviewSource.FAIL,
        showLoading: false,
      }
    }

    // Early return: if nothing exists, return null state
    if (!imageUrl && !design_content && !empty && !is_generating && !image_url_generating && !content) {
      return {
        previewSource: ImagePreviewSource.NONE,
        showLoading: false,
      }
    }

    // Priority 1: Image URL (if present and design hasn't been rendered yet)
    if (imageUrl && !hasRenderedDesignContentRef.current && !is_design_blank) {
      return {
        previewSource: ImagePreviewSource.IMAGE_URL,
        showLoading: !isImageLoaded,
      }
    }

    // Priority 2: Design content (if present, not mobile, and has dimensions)
    if (design_content && !isMobile && width && height && !is_design_blank) {
      hasRenderedDesignContentRef.current = true
      return {
        previewSource: ImagePreviewSource.DESIGN,
        showLoading: !isDesignLoaded,
      }
    }

    // Priority 3: Doc text (if showDocText is true)
    if (showDocText) {
      return {
        previewSource: ImagePreviewSource.TEXT,
        showLoading: false,
      }
    }

    // Priority 4: Loading state (if within preview wait time and loading)
    if (isWithinPreviewImageWaitTime && (isLoading || isLoadingDocPreview)) {
      return {
        previewSource: ImagePreviewSource.LOADING,
        showLoading: true,
      }
    }

    // Priority 5: Empty state
    return {
      previewSource: ImagePreviewSource.EMPTY,
      showLoading: false,
    }
  }, [event, isImageLoaded, isDesignLoaded, isCopilot, didGenerationFail, isMobile])

  return state
}
